import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

const RetryNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const maxRetries = data.config?.maxRetries || 3;
  const currentAttempt = data.executionData?.attempt || 0;

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-blue-400 to-blue-500 border-2 transition-all duration-200 ${
      selected ? 'border-blue-600 shadow-xl scale-105' : 'border-blue-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <ArrowPathIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-blue-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Retry display */}
          <div className="flex items-center mt-1 space-x-2">
            <span className="text-xs text-blue-100">
              🔄 Max: {maxRetries}x
            </span>
            {currentAttempt > 0 && (
              <span className="text-xs text-blue-100">
                | Kísérlet: {currentAttempt}
              </span>
            )}
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution result */}
      {data.executionData?.retried && (
        <div className="mt-2 text-xs text-blue-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <strong>Újrapróbálva:</strong> {data.executionData.attempt}/{maxRetries}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-blue-600 border-2 border-white"
      />

      {/* Output handles - success and failed */}
      <Handle
        type="source"
        position={Position.Right}
        id="success"
        style={{ top: '40%' }}
        className="w-3 h-3 bg-green-600 border-2 border-white"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="failed"
        style={{ top: '60%' }}
        className="w-3 h-3 bg-red-600 border-2 border-white"
      />
    </div>
  );
};

export default RetryNode;
